import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Ticket } from "lucide-react";

import { apiError } from "../../shared/api/client";
import {
  couponQueryKeys,
  couponsApi,
  type Coupon,
  type CouponForm,
  type DiscountType,
  type NewPatientRule,
  NEW_PATIENT_RULE_LABELS,
} from "../../shared/api/coupons";
import { formatVndWithUnit, isoDate } from "../../shared/format";
import { PageHeader } from "../../shared/ui/PageHeader";
import { EntitlementGate } from "../../shared/entitlement/EntitlementGate";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

/** The fixed prefix every clinic coupon code carries, mirroring what the backend stores. */
// Ngày theo giờ địa phương (#19847), không phải ngày UTC.
const TODAY = () => isoDate(new Date());

/**
 * Mã giảm giá / khuyến mãi (#18723) — the clinic-facing catalogue for #18619.
 *
 * <p>Wrapped in {@link EntitlementGate}: coupons are Pro, and every endpoint behind this screen
 * answers 402 on Lite. The gate is for clarity, not security — a Lite clinic that reached the URL
 * would get 402s rather than data.
 */
export function CouponsPage() {
  return (
    <EntitlementGate code="module.coupon">
      <CouponsContent />
    </EntitlementGate>
  );
}

function CouponsContent() {
  const qc = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: couponQueryKeys.list(!showInactive),
    queryFn: () => couponsApi.list(!showInactive),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => couponsApi.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: couponQueryKeys.all }),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Mã giảm giá"
        description="Chương trình khuyến mãi của phòng khám. Mã được áp khi lập hoá đơn."
        actions={
          <Button onClick={() => { setCreating(true); setEditing(null); }}>
            <Plus className="mr-1.5 size-4" />
            Tạo chương trình
          </Button>
        }
      />

      {(creating || editing) && (
        <CouponFormCard
          initial={editing}
          onDone={() => { setCreating(false); setEditing(null); }}
        />
      )}

      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between border-b px-4 py-2.5">
            <span className="text-sm text-muted-foreground">
              {list.data ? `${list.data.length} chương trình` : "Đang tải…"}
            </span>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Hiện cả mã đã tắt
            </label>
          </div>

          {list.isLoading && <Skeleton className="m-4 h-24" />}
          {list.isError && (
            <p className="p-4 text-sm text-destructive">{apiError(list.error).detail}</p>
          )}
          {list.data?.length === 0 && (
            <div className="flex flex-col items-center gap-2 p-10 text-center text-muted-foreground">
              <Ticket className="size-8" />
              <p className="text-sm">Chưa có chương trình khuyến mãi nào.</p>
            </div>
          )}

          {!!list.data?.length && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mã</TableHead>
                  <TableHead>Tên chương trình</TableHead>
                  <TableHead>Mức giảm</TableHead>
                  <TableHead>Hiệu lực</TableHead>
                  <TableHead>Giới hạn</TableHead>
                  <TableHead className="text-right">Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-mono font-medium">{c.code}</TableCell>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{describeDiscount(c)}</TableCell>
                    <TableCell className="text-sm">
                      <div>{describeWindow(c)}</div>
                      {!c.active ? (
                        <Badge variant="secondary">Đã tắt</Badge>
                      ) : c.withinWindow ? (
                        <Badge>Đang chạy</Badge>
                      ) : (
                        <Badge variant="secondary">Ngoài thời hạn</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {describeLimits(c)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setEditing(c); setCreating(false); }}
                      >
                        Sửa
                      </Button>
                      {c.active && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => deactivate.mutate(c.id)}
                          disabled={deactivate.isPending}
                        >
                          Tắt
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** "Giảm 20% (tối đa 200.000 ₫)" / "Giảm 150.000 ₫". */
function describeDiscount(c: Coupon): string {
  if (c.discountType === "PERCENT") {
    const cap = c.maxDiscountVnd ? ` (tối đa ${formatVndWithUnit(c.maxDiscountVnd)})` : "";
    return `Giảm ${c.discountValue}%${cap}`;
  }
  return `Giảm ${formatVndWithUnit(c.discountValue)}`;
}

function describeWindow(c: Coupon): string {
  const from = new Date(c.validFrom).toLocaleDateString("vi-VN");
  return c.validTo ? `${from} → ${new Date(c.validTo).toLocaleDateString("vi-VN")}` : `Từ ${from}`;
}

function describeLimits(c: Coupon): string {
  const parts: string[] = [];
  if (c.maxRedemptions) parts.push(`${c.maxRedemptions} lượt`);
  if (c.maxRedemptionsPerPatient) parts.push(`${c.maxRedemptionsPerPatient} lượt/bệnh nhân`);
  if (c.minInvoiceVnd > 0) parts.push(`HĐ từ ${formatVndWithUnit(c.minInvoiceVnd)}`);
  if (c.newPatientRule) {
    parts.push(
      c.newPatientRule === "REGISTERED_WITHIN_DAYS"
        ? `Khách mới (đăng ký ≤ ${c.newPatientWithinDays} ngày)`
        : `Khách mới (${NEW_PATIENT_RULE_LABELS[c.newPatientRule].toLowerCase()})`
    );
  }
  return parts.length ? parts.join(" · ") : "Không giới hạn";
}

/** Create / edit. `code` is read-only when editing — the backend refuses to change it. */
function CouponFormCard({ initial, onDone }: { initial: Coupon | null; onDone: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CouponForm>(() =>
    initial
      ? {
          code: initial.code,
          name: initial.name,
          description: initial.description,
          discountType: initial.discountType,
          discountValue: initial.discountValue,
          maxDiscountVnd: initial.maxDiscountVnd,
          minInvoiceVnd: initial.minInvoiceVnd,
          validFrom: initial.validFrom.slice(0, 10),
          validTo: initial.validTo ? initial.validTo.slice(0, 10) : null,
          maxRedemptions: initial.maxRedemptions,
          maxRedemptionsPerPatient: initial.maxRedemptionsPerPatient,
          newPatientRule: initial.newPatientRule,
          newPatientWithinDays: initial.newPatientWithinDays,
          active: initial.active,
        }
      : {
          code: "",
          name: "",
          discountType: "PERCENT",
          discountValue: 10,
          minInvoiceVnd: 0,
          validFrom: TODAY(),
          active: true,
        }
  );

  const save = useMutation({
    mutationFn: () => {
      // Dates come from <input type="date">; the API wants an instant.
      const body: CouponForm = {
        ...form,
        validFrom: new Date(`${form.validFrom}T00:00:00`).toISOString(),
        validTo: form.validTo ? new Date(`${form.validTo}T23:59:59`).toISOString() : null,
        maxDiscountVnd: form.discountType === "PERCENT" ? form.maxDiscountVnd || null : null,
        // The API rejects a day count on any other rule rather than ignoring it, so drop it here
        // instead of sending a value the user can no longer see.
        newPatientWithinDays:
          form.newPatientRule === "REGISTERED_WITHIN_DAYS" ? form.newPatientWithinDays || null : null,
      };
      // Both branches are awaited only for their success/failure; the create id is unused,
      // so normalise to void rather than widen the mutation's type.
      return initial
        ? couponsApi.update(initial.id, body)
        : couponsApi.create(body).then(() => undefined);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: couponQueryKeys.all });
      onDone();
    },
  });

  const set = <K extends keyof CouponForm>(k: K, v: CouponForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <h3 className="text-sm font-semibold">
          {initial ? `Sửa chương trình ${initial.code}` : "Chương trình mới"}
        </h3>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="code">Mã</Label>
            <Input
              id="code"
              value={form.code}
              disabled={!!initial}
              placeholder="TET2026"
              onChange={(e) => set("code", e.target.value.toUpperCase())}
            />
            {initial && (
              <p className="mt-1 text-xs text-muted-foreground">
                Mã không sửa được — mã đã phát cho bệnh nhân và các lượt đã dùng trỏ về nó. Muốn đổi
                thì tắt mã này và tạo mã mới.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="name">Tên chương trình</Label>
            <Input
              id="name"
              value={form.name}
              placeholder="Khuyến mãi Tết 2026"
              onChange={(e) => set("name", e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="dtype">Loại giảm</Label>
            <select
              id="dtype"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.discountType}
              onChange={(e) => set("discountType", e.target.value as DiscountType)}
            >
              <option value="PERCENT">Theo phần trăm (%)</option>
              <option value="AMOUNT">Số tiền cố định (₫)</option>
            </select>
          </div>
          <div>
            <Label htmlFor="dval">
              {form.discountType === "PERCENT" ? "Phần trăm giảm" : "Số tiền giảm (₫)"}
            </Label>
            <Input
              id="dval"
              type="number"
              min={1}
              max={form.discountType === "PERCENT" ? 100 : undefined}
              value={form.discountValue}
              onChange={(e) => set("discountValue", Number(e.target.value))}
            />
          </div>

          {form.discountType === "PERCENT" && (
            <div>
              <Label htmlFor="cap">Giảm tối đa (₫)</Label>
              <Input
                id="cap"
                type="number"
                min={0}
                value={form.maxDiscountVnd ?? ""}
                placeholder="Để trống = không giới hạn"
                onChange={(e) => set("maxDiscountVnd", e.target.value ? Number(e.target.value) : null)}
              />
            </div>
          )}
          <div>
            <Label htmlFor="minInv">Hoá đơn tối thiểu (₫)</Label>
            <Input
              id="minInv"
              type="number"
              min={0}
              value={form.minInvoiceVnd ?? 0}
              onChange={(e) => set("minInvoiceVnd", Number(e.target.value))}
            />
          </div>

          <div>
            <Label htmlFor="from">Bắt đầu</Label>
            <Input
              id="from"
              type="date"
              value={form.validFrom.slice(0, 10)}
              onChange={(e) => set("validFrom", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="to">Kết thúc</Label>
            <Input
              id="to"
              type="date"
              value={form.validTo?.slice(0, 10) ?? ""}
              placeholder="Để trống = tới khi tắt"
              onChange={(e) => set("validTo", e.target.value || null)}
            />
          </div>

          <div>
            <Label htmlFor="maxRed">Tổng số lượt</Label>
            <Input
              id="maxRed"
              type="number"
              min={1}
              value={form.maxRedemptions ?? ""}
              placeholder="Để trống = không giới hạn"
              onChange={(e) => set("maxRedemptions", e.target.value ? Number(e.target.value) : null)}
            />
          </div>
          <div>
            <Label htmlFor="maxPer">Lượt mỗi bệnh nhân</Label>
            <Input
              id="maxPer"
              type="number"
              min={1}
              value={form.maxRedemptionsPerPatient ?? ""}
              placeholder="Để trống = không giới hạn"
              onChange={(e) =>
                set("maxRedemptionsPerPatient", e.target.value ? Number(e.target.value) : null)
              }
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="npr">Chỉ dành cho khách hàng mới</Label>
            <select
              id="npr"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.newPatientRule ?? ""}
              onChange={(e) =>
                set("newPatientRule", (e.target.value || null) as NewPatientRule | null)
              }
            >
              <option value="">Không giới hạn — mọi bệnh nhân</option>
              {(Object.keys(NEW_PATIENT_RULE_LABELS) as NewPatientRule[]).map((r) => (
                <option key={r} value={r}>
                  {NEW_PATIENT_RULE_LABELS[r]}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              "Khách hàng mới" nghĩa là gì do phòng khám chọn cho từng chương trình — ưu đãi khai
              trương và chương trình giới thiệu bạn bè thường hiểu khác nhau.
            </p>
          </div>
          {form.newPatientRule === "REGISTERED_WITHIN_DAYS" && (
            <div>
              <Label htmlFor="nprDays">Trong vòng (ngày)</Label>
              <Input
                id="nprDays"
                type="number"
                min={1}
                value={form.newPatientWithinDays ?? ""}
                placeholder="VD: 30"
                onChange={(e) =>
                  set("newPatientWithinDays", e.target.value ? Number(e.target.value) : null)
                }
              />
            </div>
          )}
        </div>

        {save.isError && (
          <p className="text-sm text-destructive">{apiError(save.error).detail}</p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onDone}>
            Huỷ
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || !form.code.trim() || !form.name.trim()}
          >
            {save.isPending ? "Đang lưu…" : "Lưu"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
